import { toast } from "sonner";
import { IntegrationFormState, IntegrationType } from "./types";

export const integrationTypes: IntegrationType[] = [
  { id: "text_document", label: "Text Document", enabled: true },
  { id: "api", label: "API", enabled: true },
  { id: "mcp", label: "MCP", enabled: false },
];

export const getTypeLabel = (sourceType: string): string => {
  if (sourceType === "text_document") return "DOC";
  if (sourceType === "api") return "API";
  if (sourceType === "mcp") return "MCP";
  return sourceType.toUpperCase();
};

export const getDisplayName = (source: any): string => {
  if (source.source_type === "text_document") {
    const baseUrl = source.config?.base_url || "";
    const domain = baseUrl.replace(/^https?:\/\//, "").split("/")[0];
    return domain || baseUrl;
  } else if (source.source_type === "api") {
    const baseUrl = source.config?.base_url || "";
    const domain = baseUrl.replace(/^https?:\/\//, "").split("/")[0];
    return domain || baseUrl;
  } else if (source.source_type === "mcp") {
    const serverUrl = source.config?.server_url || "";
    const domain = serverUrl.replace(/^https?:\/\//, "").split("/")[0];
    return domain || serverUrl;
  }
  return "Unknown";
};

export const formatAgentId = (agentId: string): string => {
  if (agentId.length <= 8) return agentId;
  return `${agentId.slice(0, 4)}...${agentId.slice(-4)}`;
};

export const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const validateFormData = (
  selectedType: "text_document" | "api" | "mcp" | null,
  formData: IntegrationFormState
): boolean => {
  if (!selectedType) {
    toast.error("Please select an integration type");
    return false;
  }

  if (selectedType === "text_document" && !formData.base_url) {
    toast.error("Please fill in the context endpoint");
    return false;
  }

  if (selectedType === "text_document" && formData.base_url && !isValidUrl(formData.base_url)) {
    toast.error("Context endpoint must be a valid URL");
    return false;
  }

  if (
    selectedType === "text_document" &&
    formData.access_type === "private" &&
    !formData.credential_type
  ) {
    toast.error("Please select a credential type");
    return false;
  }

  if (selectedType === "api" && !formData.base_url) {
    toast.error("Please fill in the base URL");
    return false;
  }

  if (selectedType === "api" && !isValidUrl(formData.base_url)) {
    toast.error("Base URL must be a valid URL");
    return false;
  }

  if (selectedType === "api" && !formData.capabilities_path) {
    toast.error("Please provide a capabilities endpoint path");
    return false;
  }

  if (selectedType === "api" && !formData.credential_type) {
    toast.error("Please select a credential type");
    return false;
  }

  if (selectedType === "mcp" && !formData.server_url) {
    toast.error("Please fill in the MCP server URL");
    return false;
  }

  return true;
};

export const buildCredentialValue = (
  selectedType: "text_document" | "api" | "mcp",
  formData: IntegrationFormState
): string | null => {
  let credentialValue: string | null = null;

  if (selectedType === "text_document") {
    if (formData.access_type === "public") {
      credentialValue = formData.custom_headers || null;
    } else {
      if (formData.credential_type === "basic_auth") {
        credentialValue = btoa(`${formData.username || ""}:${formData.password || ""}`);
      } else if (formData.credential_type === "bearer_token") {
        credentialValue = formData.token || null;
      } else if (formData.credential_type === "api_key_header") {
        if (!formData.api_key) {
          toast.error("Please provide API key");
          return null;
        }
        credentialValue = formData.api_key;
      } else if (formData.credential_type === "oauth2") {
        credentialValue = formData.token || null;
      }
    }
  } else {
    if (formData.credential_type === "basic_auth") {
      credentialValue = btoa(`${formData.username || ""}:${formData.password || ""}`);
    } else if (formData.credential_type === "bearer_token") {
      credentialValue = formData.token || null;
    } else if (formData.credential_type === "api_key_header") {
      if (!formData.api_key) {
        toast.error("Please provide API key");
        return null;
      }
      credentialValue = formData.api_key;
    } else if (formData.credential_type === "oauth2") {
      credentialValue = formData.token || null;
    }
  }

  if (!credentialValue && selectedType !== "text_document") {
    toast.error("Please provide credential value");
    return null;
  }

  return credentialValue || "";
};

export const buildPayload = (
  selectedType: "text_document" | "api" | "mcp" | null,
  formData: IntegrationFormState,
  credentialValue: string | null
) => {
  if (!selectedType) return null;

  return {
    source_type: selectedType,
    config:
      selectedType === "text_document"
        ? {
            base_url: formData.base_url,
            access_type: formData.access_type,
            ...(formData.access_type === "public" && { custom_headers: formData.custom_headers }),
            ...(formData.access_type === "private" && {
              credential_type: formData.credential_type,
              ...(formData.credential_type === "api_key_header" && {
                auth_header_name: formData.auth_header_name,
              }),
            }),
          }
        : selectedType === "api"
          ? {
              base_url: formData.base_url,
              capabilities_path: formData.capabilities_path,
              ...(formData.credential_type === "api_key_header" &&
                formData.auth_header_name && { auth_header_name: formData.auth_header_name }),
            }
          : selectedType === "mcp"
            ? { server_url: formData.server_url }
            : {},
    credential_value: credentialValue || "",
  };
};
